<template>
    <div v-if="frame" class="indesign-overlay">
        <div
            class="indesign-overlay__frame"
            :class="{ 'indesign-overlay__frame--dragging': isDragging }"
            data-testid="indesign-overlay__frame"
            :style="frameStyle"
            @pointerdown="onPointerDown"
        >
            <span class="indesign-overlay__label">{{ label }}</span>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref } from "vue";

type Bounds = { top: number; left: number; bottom: number; right: number };

const props = defineProps<{
    /** Page units, already where the frame sits */
    bounds?: Bounds;
    pageWidth: number;
    pageHeight: number;
    /** Current offset - the base a drag adds to, never added to the drawing */
    offsetX: number;
    offsetY: number;
    label: string;
}>();

const emit = defineEmits<(e: "commit", value: { offsetX: number; offsetY: number }) => void>();

const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });
let releaseDragListeners: (() => void) | null = null;

// A drag can outlive the component
onBeforeUnmount(() => releaseDragListeners?.());

const frame = computed(() => {
    if (!props.bounds || props.pageWidth <= 0 || props.pageHeight <= 0) {
        return null;
    }

    return {
        left: props.bounds.left,
        top: props.bounds.top,
        width: props.bounds.right - props.bounds.left,
        height: props.bounds.bottom - props.bounds.top
    };
});

// Percentages, so the outline tracks the preview at any rendered size
const frameStyle = computed(() => {
    if (!frame.value) {
        return {};
    }

    const toPercent = (value: number, total: number) => `${(value / total) * 100}%`;

    return {
        left: toPercent(frame.value.left, props.pageWidth),
        top: toPercent(frame.value.top, props.pageHeight),
        width: toPercent(frame.value.width, props.pageWidth),
        height: toPercent(frame.value.height, props.pageHeight),
        transform: `translate(${dragOffset.value.x}px, ${dragOffset.value.y}px)`
    };
});

function onPointerDown(event: PointerEvent) {
    const overlay = (event.currentTarget as HTMLElement).parentElement;

    if (!overlay) {
        return;
    }

    const { width, height } = overlay.getBoundingClientRect();

    if (!width || !height) {
        return;
    }

    const startX = event.clientX;
    const startY = event.clientY;

    isDragging.value = true;
    dragOffset.value = { x: 0, y: 0 };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    event.preventDefault();

    const onMove = (moveEvent: PointerEvent) => {
        dragOffset.value = { x: moveEvent.clientX - startX, y: moveEvent.clientY - startY };
    };

    const onUp = (upEvent: PointerEvent) => {
        releaseDragListeners?.();
        isDragging.value = false;
        dragOffset.value = { x: 0, y: 0 };

        const movedX = ((upEvent.clientX - startX) / width) * props.pageWidth;
        const movedY = ((upEvent.clientY - startY) / height) * props.pageHeight;

        if (!movedX && !movedY) {
            return;
        }

        emit("commit", {
            offsetX: round(props.offsetX + movedX),
            offsetY: round(props.offsetY + movedY)
        });
    };

    releaseDragListeners = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
        releaseDragListeners = null;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
}

function round(value: number) {
    return Math.round(value * 100) / 100;
}
</script>

<style lang="scss" scoped>
.indesign-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    // A frame on a later page has bounds outside this one
    overflow: hidden;

    &__frame {
        position: absolute;
        box-sizing: border-box;
        border: 1px solid #2d8cf0;
        background: rgb(45 140 240 / 8%);
        cursor: grab;
        pointer-events: auto;
        touch-action: none;

        &--dragging {
            cursor: grabbing;
            border-style: dashed;
        }
    }

    &__label {
        position: absolute;
        bottom: 100%;
        left: 0;
        padding: 1px 4px;
        font-size: 10px;
        line-height: 1.4;
        white-space: nowrap;
        color: #fff;
        background: #2d8cf0;
        pointer-events: none;
    }
}
</style>
